/* global Office, Word, console */

/**
 * Document Extraction Utilities
 *
 * Functions for extracting document content from Word
 * for use with the styling classification API.
 */

import {
  buildFormattingInfo,
  queueMixedItalicCharLoads,
  readMixedItalicChars,
  StepProgress,
  WORD_DELIMITERS,
} from "src/services";
import { trackEvent } from "src/services/telemetry";

import type {
  ApplyProgressReporter,
  FormattingSnapshot,
  MixedItalicChars,
  ParagraphFormattingInfo,
  PerfMeta,
  PerfSession,
} from "src/services";
import type { ClassificationLine, ClassificationResponse } from "src/types";

// Tag used to identify the lock content control.
const LOCK_TAG = "mss-processing-lock";

/** Paragraph-level font aggregates, enough to rule out any character-style rule firing. */
interface ParagraphFontAggregates {
  italic?: boolean;
  bold?: boolean;
  underline?: string;
  strikeThrough?: boolean;
}

/**
 * Whether a paragraph could possibly produce character-style actions, decided
 * from paragraph-level aggregates alone so the per-word scan can be skipped.
 *
 * Word's paragraph `Font` reports `false` only when *no* character in the
 * paragraph carries the attribute, and `null` when it is mixed; `underline`
 * uses the string `"None"` / `"Mixed"`. So `=== false` and `=== "None"` are
 * proofs of absence, while mixed, unloaded, and undefined all fall through to
 * true — every ambiguous case keeps the expensive path.
 *
 * Every action-emitting rule needs either an entity hint or a per-span
 * italic/bold/underline/strikethrough flag, so absence of all four with no
 * hints means the rules engine provably returns nothing.
 *
 * IMPORTANT: re-enabling the SmallCaps rules (SC-1..SC-4 in rules-engine.ts)
 * invalidates this — they trigger on `textIsUppercase`, which has no
 * paragraph-level aggregate. Add a text-based clause here if they come back.
 */
function needsWordRanges(
  line: ClassificationResponse["lines"][number],
  text: string,
  font: ParagraphFontAggregates
): boolean {
  if ((line.entity_hints?.length ?? 0) > 0) return true;
  if (text.trim() === "") return false;
  return (
    font.italic !== false ||
    font.bold !== false ||
    font.strikeThrough !== false ||
    font.underline !== "None"
  );
}

/**
 * Formatting for a paragraph proven to carry no italic/bold/underline/
 * strikethrough. Written to the snapshot in place of a real scan so
 * `applyCharacterStyles` still finds an entry for every paragraph — a missing
 * entry there means "read formatting live", which is the cost being avoided.
 */
function plainFormatting(text: string): ParagraphFormattingInfo {
  return {
    text,
    spans: text
      ? [
          {
            start: 0,
            end: text.length,
            text,
            italic: false,
            bold: false,
            underline: false,
            strikethrough: false,
            characterStyle: null,
            smallCaps: false,
            textIsUppercase: false,
          },
        ]
      : [],
    majorityItalic: false,
    allItalic: false,
    allBold: false,
    allUppercase: false,
  };
}

/**
 * Enables track changes in the Word document if not already enabled
 *
 * @param onStatusChange - Optional callback for status updates
 * @returns Promise resolving to true if tracking was enabled successfully
 */
export async function enableTrackChanges(
  onStatusChange?: (message: string) => void
): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      context.document.load("changeTrackingMode");
      await context.sync();

      if (context.document.changeTrackingMode !== Word.ChangeTrackingMode.trackAll) {
        onStatusChange?.("Enabling track changes...");
        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        await context.sync();
      }

      return true;
    });
  } catch (error) {
    console.error("Error enabling track changes:", error);
    throw new Error(
      `Failed to enable track changes: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Extracts all paragraphs from the Word document
 *
 * @param perf - Optional perf session for per-sync timing attribution
 * @returns Promise resolving to array of classification lines
 */
export async function extractDocumentLines(perf?: PerfSession): Promise<ClassificationLine[]> {
  try {
    return await Word.run(async (context) => {
      const sync = (name: string, meta?: () => Record<string, number | string | boolean>) =>
        perf ? perf.timedSync(context, name, meta) : context.sync();

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await sync("extract:loadItems", () => ({ paragraphs: paragraphs.items.length }));

      for (const paragraph of paragraphs.items) {
        paragraph.load(["text", "style", "uniqueLocalId"]);
      }
      await sync("extract:loadProps", () => ({ paragraphs: paragraphs.items.length }));

      // Convert to classification lines
      const lines: ClassificationLine[] = paragraphs.items.map((paragraph, index) => ({
        index,
        raw_text: paragraph.text?.trim() || "",
        raw_style: paragraph.style || "Normal",
        paragraph_id: paragraph.uniqueLocalId || undefined,
      }));

      return lines;
    });
  } catch (error) {
    console.error("Error extracting document lines:", error);
    throw new Error(
      `Failed to extract document content: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/** Pure: turn loaded word ranges into the formatting snapshot. Timed separately to
 * distinguish Office.js bridge cost from JS cost. */
function buildSnapshot(
  rangeLoads: Array<{
    paragraphId: string;
    text: string;
    wordRanges: Word.RangeCollection;
    mixedItalicChars?: MixedItalicChars;
  }>,
  formattingSnapshot: FormattingSnapshot
): void {
  for (const { paragraphId, text, wordRanges, mixedItalicChars } of rangeLoads) {
    formattingSnapshot.set(paragraphId, buildFormattingInfo(text, wordRanges, mixedItalicChars));
  }
}

/**
 * Approximate words per scan chunk, and so Range proxies per chunk.
 *
 * `Word.run` releases its whole object store on exit, and that release is
 * superlinear in proxy count: 10k word ranges tore down in 0.7-2.5s while 44k
 * took 80s (1.81ms per proxy vs 0.07ms). Chunking keeps every teardown in the
 * cheap regime. Counted in words rather than paragraphs because `getTextRanges`
 * yields one proxy per word, and paragraph lengths vary by an order of magnitude.
 */
const SCAN_WORDS_PER_CHUNK = 4000;

/**
 * Paragraph style writes per sync.
 *
 * Measured: paragraph style writes are close to linear in count, so this bounds
 * each sync for progress reporting and error isolation rather than for speed —
 * chunking 6,621 writes gained only 23%. Lowering it will not pay. What does
 * move the cost is how many revisions the document already carries: the same
 * 500 writes cost 5.0s early in a run and 8.0s after 6,000, because every write
 * is a revision under trackAll.
 */
const STYLE_WRITES_PER_BATCH = 500;
const LIST_RESTORE_PER_BATCH = 500;

interface ListSnapshot {
  targetIndex: number;
  paragraph: Word.Paragraph;
  listType: Word.ListType | string;
  listLevelNumber: number;
  listValue: number;
  listTemplate: Word.ListTemplate;
}

/**
 * Build the formatting snapshot in chunks, each in its own `Word.run`.
 *
 * The nested context is the point: word ranges are needed only until
 * `buildSnapshot` reads them into plain JS, and a separate context disposes
 * them at that point instead of holding every range alive until the enclosing
 * stage returns.
 *
 * A chunk that fails leaves its paragraphs absent from the snapshot, which
 * `applyCharacterStyles` treats as "read this paragraph's formatting live" —
 * slower, but correct. Writing a plain entry instead would silently claim the
 * text has no formatting and skip its character styles.
 */
async function scanFormattingInChunks(
  scanTargets: Array<{ paragraphId: string; text: string }>,
  formattingSnapshot: FormattingSnapshot,
  perf?: PerfSession,
  onProgress?: ApplyProgressReporter
): Promise<void> {
  const chunks: Array<Array<{ paragraphId: string; text: string }>> = [];
  let current: Array<{ paragraphId: string; text: string }> = [];
  let currentWords = 0;

  for (const target of scanTargets) {
    const words = target.text.trim().split(/\s+/).length;
    current.push(target);
    currentWords += words;
    if (currentWords >= SCAN_WORDS_PER_CHUNK) {
      chunks.push(current);
      current = [];
      currentWords = 0;
    }
  }
  if (current.length > 0) {
    chunks.push(current);
  }

  const flushChunk = (context: Word.RequestContext, label: string, meta: () => PerfMeta) =>
    perf ? perf.timedSync(context, label, meta) : context.sync();

  const progress = new StepProgress("Scanning formatting", chunks.length, onProgress);

  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index];
    const label = `applyPara:scanBatch${String(index + 1).padStart(2, "0")}`;
    try {
      await Word.run(async (context) => {
        const rangeLoads = chunk.map(({ paragraphId, text }) => {
          const paragraph = context.document.getParagraphByUniqueLocalId(paragraphId);
          const wordRanges = paragraph.getRange().getTextRanges(WORD_DELIMITERS, true);
          wordRanges.load(
            "items/text, items/font/italic, items/font/bold, items/font/underline, items/font/strikeThrough, items/style"
          );
          return { paragraphId, text, wordRanges };
        });

        await flushChunk(context, label, () => ({
          paragraphs: chunk.length,
          wordRanges: rangeLoads.reduce((sum, r) => sum + r.wordRanges.items.length, 0),
        }));

        // A partly-italic word such as `reinvigorate—the` needs per-character
        // italics so the italic style can stop at the punctuation. Only those
        // words are read, in one extra sync that is skipped when there are none.
        const charLoads = rangeLoads.map((r) => queueMixedItalicCharLoads(r.wordRanges));
        const mixedWords = charLoads.reduce((sum, pending) => sum + pending.size, 0);
        if (mixedWords > 0) {
          await flushChunk(context, `${label}:mixedItalic`, () => ({ mixedWords }));
        }

        buildSnapshot(
          rangeLoads.map((r, i) => ({
            ...r,
            mixedItalicChars: charLoads[i].size > 0 ? readMixedItalicChars(charLoads[i]) : undefined,
          })),
          formattingSnapshot
        );
      });
    } catch (error) {
      console.warn(
        `[applyPara] snapshot chunk ${index + 1} failed; its paragraphs will be read live:`,
        error
      );
    }
    progress.set(index + 1);
  }

  perf?.note("applyPara:scanTotal", {
    paragraphs: scanTargets.length,
    batches: chunks.length,
  });
}

/**
 * Applies classified styles back to the Word document
 *
 * The caller must have unlocked the document first: `lockDocument` sets
 * `cannotEdit`, which blocks *programmatic* format changes too, so the style
 * writes below would silently fail to flush.
 *
 * @param response - The classification response from the API
 * @param useTrackChanges - Whether to use track changes when applying styles
 * @param perf - Optional perf session for per-sync timing attribution
 * @param onProgress - Optional per-step progress reporter for the UI
 * @returns Promise resolving to number of paragraphs styled
 */
export async function applyClassifiedStyles(
  response: ClassificationResponse,
  perf?: PerfSession,
  onProgress?: ApplyProgressReporter
): Promise<{ applied: number; skipped: number; formattingSnapshot: FormattingSnapshot }> {
  try {
    return await Word.run(async (context) => {
      const sync = (name: string, meta?: () => Record<string, number | string | boolean>) =>
        perf ? perf.timedSync(context, name, meta) : context.sync();
      // Track results
      let appliedCount = 0;
      let skippedCount = 0;

      // One collection load supplies every paragraph's text, style, and font
      // aggregates. It replaces N getParagraphByUniqueLocalId lookups, and the
      // aggregates are what let the word-range scan below be skipped.
      const allParagraphs = context.document.body.paragraphs;
      allParagraphs.load(
        "items/text,items/style,items/uniqueLocalId,items/font/italic,items/font/bold,items/font/underline,items/font/strikeThrough"
      );
      // One sync, so this reports as a named step with no count. It is worth
      // naming anyway: it reached 19.6s on a heavily-revised manuscript.
      const readProgress = new StepProgress("Reading document structure", 1, onProgress);
      await sync("applyPara:resolveLookups", () => ({
        paragraphs: allParagraphs.items.length,
      }));
      readProgress.done();

      const byLocalId = new Map<string, Word.Paragraph>();
      for (const paragraph of allParagraphs.items) {
        if (paragraph.uniqueLocalId) {
          byLocalId.set(paragraph.uniqueLocalId, paragraph);
        }
      }

      // Resolve each response line to its target paragraph via paragraph_id.
      // Lines without a paragraph_id are skipped (left at their current style).
      const targets: Array<{
        paragraph: Word.Paragraph;
        classifiedLine: (typeof response.lines)[number];
      }> = [];

      for (const classifiedLine of response.lines) {
        const newStyle = classifiedLine.style;

        // Skip if no style is provided
        if (!newStyle || typeof newStyle !== "string") {
          console.warn(`No style provided for line ${classifiedLine.index}, skipping`);
          skippedCount++;
          continue;
        }

        if (!classifiedLine.paragraph_id) {
          console.warn(`No paragraph_id for line ${classifiedLine.index}, skipping`);
          skippedCount++;
          continue;
        }

        const paragraph = byLocalId.get(classifiedLine.paragraph_id);
        if (!paragraph) {
          console.warn(
            `Paragraph ${classifiedLine.paragraph_id} for line ${classifiedLine.index} not found, skipping`
          );
          skippedCount++;
          continue;
        }

        targets.push({ paragraph, classifiedLine });
      }

      // ── Formatting snapshot ──────────────────────────────────
      // Capture word-level formatting *before* paragraph styles are
      // applied. Word resets direct formatting (italic, bold,
      // underline, strikethrough) when a paragraph style changes, so
      // this snapshot preserves the original state for character
      // style rules evaluated later.
      //
      // Paragraphs whose font aggregates prove no formatting is present get a
      // synthetic plain entry instead of a per-word scan. Every paragraph still
      // gets an entry: `applyCharacterStyles` reads a missing one as "re-read
      // formatting live", which would undo the saving.
      const formattingSnapshot: FormattingSnapshot = new Map();

      const scanTargets: Array<{ paragraphId: string; text: string }> = [];
      let plainCount = 0;

      for (const { paragraph, classifiedLine } of targets) {
        const paragraphId = classifiedLine.paragraph_id!;
        const text = paragraph.text ?? "";

        if (!needsWordRanges(classifiedLine, text, paragraph.font)) {
          formattingSnapshot.set(paragraphId, plainFormatting(text));
          plainCount++;
          continue;
        }

        scanTargets.push({ paragraphId, text });
      }

      perf?.note("applyPara:scanPlanned", {
        scanned: scanTargets.length,
        plain: plainCount,
      });
      await scanFormattingInChunks(scanTargets, formattingSnapshot, perf, onProgress);

      // ── Capture list formatting before style changes ─────────
      // Word strips native list numbering when a paragraph style is
      // reassigned. Snapshot each paragraph's list state so it can be
      // restored after the style writes land.
      // Phase 1: load list type to find which paragraphs have lists.
      const listFormats = targets.map(({ paragraph }) => {
        const lf = paragraph.getRange().listFormat;
        lf.load(["listType", "listLevelNumber", "listValue"]);
        return lf;
      });

      await sync("applyPara:captureListType");

      // Phase 2: for paragraphs that have lists, pin the template's
      // server-side identity by loading a property on it.  Without this
      // the proxy is a navigation path that re-resolves after the style
      // change — pointing at whatever template the new style brought.
      const listIndices: number[] = [];
      const pinnedTemplates: Word.ListTemplate[] = [];
      for (let i = 0; i < targets.length; i++) {
        if ((listFormats[i].listType as string) !== "ListNoNumbering") {
          const lt = listFormats[i].listTemplate;
          lt.load(["outlineNumbered", "name"]);
          listIndices.push(i);
          pinnedTemplates.push(lt);
        }
      }

      if (listIndices.length > 0) {
        await sync("applyPara:pinListTemplates");
      }

      const listSnapshots: ListSnapshot[] = [];
      for (let j = 0; j < listIndices.length; j++) {
        const i = listIndices[j];
        const lt = pinnedTemplates[j];
        if (!lt.isNullObject) {
          listSnapshots.push({
            targetIndex: i,
            paragraph: targets[i].paragraph,
            listType: listFormats[i].listType,
            listLevelNumber: listFormats[i].listLevelNumber,
            listValue: listFormats[i].listValue,
            listTemplate: lt,
          });
        }
      }

      // ── Diagnostic: log captured list state ─────────────────
      // for (const snap of listSnapshots) {
      //   const style = targets[snap.targetIndex].classifiedLine.style;
      //   const text = targets[snap.targetIndex].paragraph.text?.substring(0, 50);
      //   console.warn(
      //     `[listPreserve:capture] idx=${snap.targetIndex} style="${style}" ` +
      //       `text="${text}" listType=${snap.listType} level=${snap.listLevelNumber} ` +
      //       `value=${snap.listValue} ` +
      //       `templateName="${snap.listTemplate.name}" outline=${snap.listTemplate.outlineNumbered}`
      //   );
      // }

      perf?.note("applyPara:listCapture", {
        withList: listSnapshots.length,
        total: targets.length,
      });

      // ── Apply paragraph style changes ────────────────────────
      let styleBatchIndex = 0;
      let styleBatchWrites = 0;
      let processedTargets = 0;

      // Counted in paragraphs considered rather than styles written, because the
      // number of writes is not known until the loop has been all the way
      // through and a bar has to have its total up front.
      const styleProgress = new StepProgress(
        "Applying paragraph styles",
        targets.length,
        onProgress
      );

      const flushStyleBatch = async () => {
        if (styleBatchWrites === 0) return;
        styleBatchIndex += 1;
        const writes = styleBatchWrites;
        styleBatchWrites = 0;
        const label = `applyPara:styleBatch${String(styleBatchIndex).padStart(2, "0")}`;
        try {
          await sync(label, () => ({ writes, cumulativeWrites: appliedCount }));
        } catch (batchError) {
          // Independent writes, so one bad batch shouldn't cost the rest of the
          // document its styles. The queued writes never landed, so move them
          // from applied to skipped rather than reporting them as successes.
          console.warn(`[applyPara] style batch ${styleBatchIndex} failed:`, batchError);
          appliedCount -= writes;
          skippedCount += writes;
        }
        styleProgress.set(processedTargets);
      };

      for (const { paragraph, classifiedLine } of targets) {
        processedTargets++;
        const newStyle = classifiedLine.style;
        const currentStyle = paragraph.style || "Normal";

        if (typeof currentStyle !== "string") {
          console.warn(
            `Current style is not a string for paragraph ${classifiedLine.index}:`,
            currentStyle
          );
          skippedCount++;
          continue;
        }

        // Only apply if the style is different
        if (currentStyle.toLowerCase() === newStyle.toLowerCase()) {
          skippedCount++;
          continue;
        }

        try {
          paragraph.style = newStyle;
          appliedCount++;
          styleBatchWrites++;
        } catch (styleError) {
          console.error(
            `Failed to apply style "${newStyle}" to paragraph ${classifiedLine.index}:`,
            styleError
          );
          skippedCount++;
        }

        if (styleBatchWrites >= STYLE_WRITES_PER_BATCH) {
          await flushStyleBatch();
        }
      }
      await flushStyleBatch();
      styleProgress.done();

      perf?.note("applyPara:styleTotal", {
        applied: appliedCount,
        skipped: skippedCount,
        batches: styleBatchIndex,
      });

      // ── Diagnostic: read-back list state after style writes ──
      // Only load listType (always safe); listString/template throw if
      // the style change stripped the list.
      if (listSnapshots.length > 0) {
        const postStyleFormats = listSnapshots.map((snap) => {
          const lf = snap.paragraph.getRange().listFormat;
          lf.load("listType");
          return lf;
        });
        await sync("applyPara:diagPostStyle");
        for (let si = 0; si < listSnapshots.length; si++) {
          const snap = listSnapshots[si];
          const pf = postStyleFormats[si];
          // const style = targets[snap.targetIndex].classifiedLine.style;
          // const stripped = (pf.listType as string) === "ListNoNumbering";
          // console.warn(
          //   `[listPreserve:postStyle] idx=${snap.targetIndex} style="${style}" ` +
          //     `listType=${pf.listType} stripped=${stripped} ` +
          //     `capturedLevel=${snap.listLevelNumber} capturedValue=${snap.listValue} ` +
          //     `capturedTemplate="${snap.listTemplate.name}"`
          // );
        }
      }

      // ── Restore list numbering stripped by style changes ─────
      if (listSnapshots.length > 0) {
        const restoreProgress = new StepProgress(
          "Restoring list numbering",
          listSnapshots.length,
          onProgress
        );
        let restoreBatchIndex = 0;
        let restoreBatchWrites = 0;
        let restoredCount = 0;
        let restoreFailedCount = 0;

        const flushRestoreBatch = async () => {
          if (restoreBatchWrites === 0) return;
          restoreBatchIndex += 1;
          const writes = restoreBatchWrites;
          restoreBatchWrites = 0;
          const label = `applyPara:listRestoreBatch${String(restoreBatchIndex).padStart(2, "0")}`;
          try {
            await sync(label, () => ({ writes, cumulativeRestores: restoredCount }));
          } catch (batchError) {
            console.warn(`[applyPara] list restore batch ${restoreBatchIndex} failed:`, batchError);
            restoredCount -= writes;
            restoreFailedCount += writes;
          }
          restoreProgress.set(restoredCount + restoreFailedCount);
        };

        for (let si = 0; si < listSnapshots.length; si++) {
          const snap = listSnapshots[si];
          const style = targets[snap.targetIndex].classifiedLine.style;
          // value 1 means "start of a list" — chaining forward renumbers it.
          const shouldContinue = snap.listValue > 1;
          // console.warn(
          //   `[listPreserve:restore] idx=${snap.targetIndex} style="${style}" ` +
          //     `applyLevel=${snap.listLevelNumber} continue=${shouldContinue} ` +
          //     `capturedValue=${snap.listValue} ` +
          //     `templateName="${snap.listTemplate.name}"`
          // );
          try {
            snap.paragraph.getRange().listFormat.applyListTemplateWithLevel(snap.listTemplate, {
              applyLevel: snap.listLevelNumber,
              continuePreviousList: shouldContinue,
              applyTo: Word.ListApplyTo.selection,
            });
            restoredCount++;
            restoreBatchWrites++;
          } catch (restoreError) {
            console.warn(
              `[applyPara] list restore failed for target ${snap.targetIndex}:`,
              restoreError
            );
            restoreFailedCount++;
          }

          if (restoreBatchWrites >= LIST_RESTORE_PER_BATCH) {
            await flushRestoreBatch();
          }
        }
        await flushRestoreBatch();
        restoreProgress.done();

        // ── Fix style-linked level overrides ──────────────────
        // Some Word styles (e.g. "nlf") are linked to a specific list
        // level via linkToListTemplate.  applyListTemplateWithLevel
        // restores the correct template but the style's linked level
        // wins, shifting the paragraph to the wrong level.  Read back
        // the actual level and correct any drift.
        const postRestoreFormats = listSnapshots.map((snap) => {
          const lf = snap.paragraph.getRange().listFormat;
          lf.load(["listType", "listLevelNumber", "listString", "listValue"]);
          return lf;
        });
        await sync("applyPara:readBackLevels");

        let levelFixCount = 0;
        for (let si = 0; si < listSnapshots.length; si++) {
          const snap = listSnapshots[si];
          const rf = postRestoreFormats[si];
          if (
            (rf.listType as string) !== "ListNoNumbering" &&
            rf.listLevelNumber !== snap.listLevelNumber
          ) {
            // console.warn(
            //   `[listPreserve:levelFix] idx=${snap.targetIndex} ` +
            //     `drifted=${rf.listLevelNumber} expected=${snap.listLevelNumber}`
            // );
            rf.listLevelNumber = snap.listLevelNumber;
            levelFixCount++;
          }
        }
        if (levelFixCount > 0) {
          await sync("applyPara:fixLevels");
        }

        // ── Diagnostic: verify final list state ───────────────
        // const verifyFormats = listSnapshots.map((snap) => {
        //   const lf = snap.paragraph.getRange().listFormat;
        //   lf.load(["listType", "listLevelNumber", "listString", "listValue"]);
        //   return lf;
        // });
        // await sync("applyPara:diagPostRestore");
        // for (let si = 0; si < listSnapshots.length; si++) {
        //   const snap = listSnapshots[si];
        //   const vf = verifyFormats[si];
        //   const style = targets[snap.targetIndex].classifiedLine.style;
        //   const ok = (vf.listType as string) !== "ListNoNumbering";
        //   console.warn(
        //     `[listPreserve:final] idx=${snap.targetIndex} style="${style}" ` +
        //       `listType=${vf.listType} level=${vf.listLevelNumber} ` +
        //       `listString="${vf.listString}" listValue=${vf.listValue} ` +
        //       `capturedValue=${snap.listValue} restored=${ok}`
        //   );
        // }

        perf?.note("applyPara:listRestoreTotal", {
          restored: restoredCount,
          failed: restoreFailedCount,
          batches: restoreBatchIndex,
        });

        trackEvent("ListPreservation_BulkApply", {
          captured: String(listSnapshots.length),
          restored: String(restoredCount),
          failed: String(restoreFailedCount),
        });
      }

      // Everything after this point is Word.run tearing down its object store,
      // which is otherwise invisible — it lands in the enclosing stage's self
      // time. Word ranges now live and die inside the scan chunks, so what is
      // left here is one proxy per paragraph rather than one per word.
      perf?.note("applyPara:beforeTeardown", {
        paragraphProxies: allParagraphs.items.length,
      });

      return {
        applied: appliedCount,
        skipped: skippedCount,
        formattingSnapshot,
      };
    });
  } catch (error) {
    console.error("Error applying classified styles:", error);
    throw new Error(
      `Failed to apply styles to document: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
/**
 * Locks the document body by wrapping it in a content control
 * with `cannotEdit` set to true. Prevents user edits while the
 * styling pipeline runs.
 *
 */
export async function lockDocument(): Promise<void> {
  try {
    await Word.run(async (context) => {
      const cc = context.document.body.insertContentControl();
      cc.tag = LOCK_TAG;
      cc.title = "Document locked - Style processing in progress";
      cc.color = "orange";
      cc.cannotEdit = true;
      cc.cannotDelete = true;
      cc.appearance = Word.ContentControlAppearance.boundingBox;
      await context.sync();
    });
  } catch (error) {
    console.error("Error locking document:", error);
    throw new Error(
      `Failed to lock document: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Unlocks the document by removing all content controls tagged with
 * the processing lock tag. Idempotent — safe to call even when no
 * lock exists (e.g. from a finally block after a successful unlock).
 */
export async function unlockDocument(): Promise<void> {
  try {
    await Word.run(async (context) => {
      const controls = context.document.contentControls.getByTag(LOCK_TAG);
      controls.load("items");
      await context.sync();

      for (const cc of controls.items) {
        cc.cannotEdit = false;
        cc.cannotDelete = false;
        cc.delete(true); // keepContent = true
      }
      await context.sync();
    });
  } catch (error) {
    console.error("Error unlocking document:", error);
    throw new Error(
      `Failed to unlock document: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Removes any orphaned lock content controls left over from a
 * previous crash or interrupted processing session. Called on
 * taskpane startup so the document is never stuck in a locked state.
 *
 * Errors are logged but swallowed — cleanup must never block
 * the taskpane from loading.
 */
export async function removeOrphanedLocks(): Promise<void> {
  try {
    await unlockDocument();
  } catch (error) {
    console.warn("Non-fatal: failed to remove orphaned lock controls:", error);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// Document Identity & Metadata
// ══════════════════════════════════════════════════════════════════════════════

// In-memory cache for document identifier (persists during session)
let cachedDocumentId: string | null = null;
let cachedDocumentName: string | null = null;

/**
 * Internal helper: Gets or creates the document ID from custom properties.
 * Assumes customProperties are already loaded.
 * @param customProps - Loaded custom properties collection
 * @returns The document ID (existing or newly created)
 */
async function getOrCreateDocumentId(
  customProps: Word.CustomPropertyCollection,
  context: Word.RequestContext
): Promise<string> {
  // Check if we already have a document ID
  const existingIdProp = customProps.items.find((p) => p.key === "MSS_DocumentId");
  if (existingIdProp) {
    cachedDocumentId = existingIdProp.value;
    return existingIdProp.value;
  }

  // Generate a new UUID for this document
  const newId = generateUUID();
  customProps.add("MSS_DocumentId", newId);
  await context.sync();
  cachedDocumentId = newId;
  return newId;
}

/**
 * Generate a stable document identifier.
 * Uses custom document property if available, otherwise creates a new UUID.
 * Optimized with in-memory caching and minimal context syncs.
 */
export async function getDocumentIdentifier(): Promise<string> {
  // Return cached value if available
  if (cachedDocumentId) {
    return cachedDocumentId;
  }

  return Word.run(async (context) => {
    const customProps = context.document.properties.customProperties;
    customProps.load("items/key, items/value");
    await context.sync();

    return getOrCreateDocumentId(customProps, context);
  });
}

/**
 * Get both document identifier and name in a single Word API call.
 * More efficient than calling getDocumentIdentifier() and getDocumentName() separately.
 */
export async function getDocumentMetadata(): Promise<{ documentId: string; documentName: string }> {
  // The title is cached too: it is only recorded for reference, so a stale value is harmless.
  if (cachedDocumentId && cachedDocumentName !== null) {
    return { documentId: cachedDocumentId, documentName: cachedDocumentName };
  }

  // Otherwise, get both in one Word.run call
  return Word.run(async (context) => {
    const props = context.document.properties;
    const customProps = props.customProperties;

    props.load("title");
    customProps.load("items/key, items/value");
    await context.sync();

    // Use helper to get or create document ID
    const documentId = await getOrCreateDocumentId(customProps, context);
    cachedDocumentName = props.title || "Untitled";

    return { documentId, documentName: cachedDocumentName };
  });
}

/**
 * The document's file name, e.g. `Follett_ADangerousFortune.docx`, or null for a
 * document that has never been saved.
 *
 * Distinct from `getDocumentName`, which reads the Title document property —
 * usually unset on a manuscript, and unrelated to what the user sees in the title
 * bar. Costs no `context.sync`, so it is cheap enough to read on every run.
 *
 * Only the basename: `Office.context.document.url` is the whole path or
 * SharePoint URL, and the rest of it is either the user's home directory or a
 * tenant address, neither of which belongs in telemetry.
 */
export function getDocumentFileName(): string | null {
  const name = (Office.context.document.url || "").split(/[\\/]/).pop();
  if (!name) return null;
  try {
    return decodeURIComponent(name);
  } catch {
    // SharePoint percent-encodes its URLs and a local path does not, so a name
    // like "Book 50% draft.docx" is not decodable and is already literal.
    return name;
  }
}

/**
 * Get current document name/title.
 */
export async function getDocumentName(): Promise<string> {
  return Word.run(async (context) => {
    const props = context.document.properties;
    props.load("title");
    await context.sync();
    return props.title || "Untitled";
  });
}

/**
 * Clear the in-memory document ID cache.
 * Call this when switching documents or when document identity changes.
 */
export function clearDocumentCache(): void {
  cachedDocumentId = null;
  cachedDocumentName = null;
}

/**
 * Generate a simple UUID (v4-like).
 */
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
